import type { Cta } from "@/content/types";

/**
 * Everything the home page says, links to and shows, in the published order.
 *
 * Editorial data, exactly like `header/navigation.ts`: nothing derives it. It
 * was read out of https://www.webshare.io/ and recorded in
 * `marketing/design/home-spec.md`, and this module is the only place a word,
 * a price or a destination is decided.
 *
 * Three blocks below carry numbers that change without a deploy, the customer
 * count, the three from-prices and the four premium prices. They are hand-kept
 * here rather than fetched, which makes this module the one place to correct
 * them when they drift. The eight country IP counts were a fourth until the
 * pricing page needed the same list; they now live in `marketing/countries.ts`.
 */

/**
 * The app routes the published page reaches through `dashboard.webshare.io`.
 * Every `?source=` key is reproduced verbatim, the site's attribution reads it
 *, and so is each query string's key order, which express checkout replays
 * into its own controls.
 */
const REGISTER = "/register";
const LOGIN = "/login";
const CHECKOUT = "/express-checkout";

/**
 * Pages this app does not serve. The published markup links them root-relative
 * because it IS the marketing site; ours has to name the host or they would
 * resolve against this origin and 404.
 */
const SITE = "https://www.webshare.io";

const ASSETS = "/marketing/home";

export const STICKY_BAR = {
  lottie: `${ASSETS}/sticky-gift.json`,
  title: "10 Proxies Free Today. No credit card required",
  cta: {
    label: "Start for Free",
    href: `${REGISTER}?source=sticky_banner_register`,
    newTab: true,
  } satisfies Cta,
};

export type HeroTile = {
  title: string;
  subtitle: string;
  /** The 32×32 glyph the tile swaps to once it collapses to a single row. */
  icon: string;
  image: string;
};

export const HOME_HERO = {
  heading: "Fast, reliable proxies you can set up yourself",
  body: "No sales calls or complex setup. Get 10 free proxies and access flexible plans for residential, ISP, and datacenter use cases.",
  chips: [
    { label: "10 proxies for free", icon: `${ASSETS}/chip-gift.svg` },
    {
      label: "No credit card required",
      icon: `${ASSETS}/chip-no-credit-card.svg`,
    },
  ],
  primaryCta: {
    label: "Get 10 Free Proxies",
    href: `${REGISTER}?source=home_hero_button_register`,
    newTab: true,
  } satisfies Cta,
  /**
   * `start_google_login_flow` is a bare valueless parameter, not `=true`; the
   * sign-in page keys off its presence, so it must not be normalised.
   */
  googleCta: {
    label: "Start with Google",
    href: `${LOGIN}?start_google_login_flow&source=home_hero_google_register`,
  } satisfies Cta,
  googleLogo: `${ASSETS}/google-g.svg`,
  videoLabel: "See How it Works",
  playIcon: `${ASSETS}/play.svg`,
  poster: `${ASSETS}/video-poster.jpg`,
  /** See `SUPPORT.cards[0].srcsetWidth` for why this is not a natural width. */
  posterSrcsetWidth: 2600,
  tiles: [
    {
      title: "Get 10 Proxies for free",
      subtitle: "No credit required",
      icon: `${ASSETS}/tile-icon-free.svg`,
      image: `${ASSETS}/tile-free.avif`,
    },
    {
      title: "99.7% Success rate",
      subtitle: "Industry-leading reliability you can count on.",
      icon: `${ASSETS}/tile-icon-success.svg`,
      image: `${ASSETS}/tile-success.avif`,
    },
    {
      title: "Customizable Plans",
      subtitle: "Choose exact proxy count, bandwidth, location, and more.",
      icon: `${ASSETS}/tile-icon-custom.svg`,
      image: `${ASSETS}/tile-custom.avif`,
    },
    {
      title: "Browser Extension",
      subtitle: "Top-rated Chrome extension—simple, intuitive, and powerful.",
      icon: `${ASSETS}/tile-icon-extension.svg`,
      image: `${ASSETS}/tile-extension.avif`,
    },
    {
      title: "24/7 Expert Support",
      subtitle:
        "Real proxy experts, available around the clock when you need help.",
      icon: `${ASSETS}/tile-icon-support.svg`,
      image: `${ASSETS}/tile-support.avif`,
    },
    {
      title: "Extensive API docs",
      subtitle: "Set up in any tool with dev-friendly docs.",
      icon: `${ASSETS}/tile-icon-api.svg`,
      image: `${ASSETS}/tile-api.avif`,
    },
    {
      title: "80M+ Global IPs",
      subtitle:
        "Access global IPs across 195 Countries. Filter down to the state, city or ZIP code when you need it.",
      icon: `${ASSETS}/tile-icon-locations.svg`,
      image: `${ASSETS}/tile-locations.avif`,
    },
  ] satisfies HeroTile[],
};

/**
 * The count moves; the two lines are a hard break, not a wrap, and the first
 * keeps the space the published line carries before its break. The logos are
 * the site's own, in `components/marketing/CustomerLogos.tsx`.
 */
export const LOGO_MARQUEE = {
  lead: "Webshare is trusted by ",
  count: "64,773 customers worldwide",
};

const SHARED_PLAN =
  "autoRefreshFrequency=0&isCustomAutoRefreshFrequency=false" +
  "&isUnlimitedIpAuthorizations=false&isHighConcurrency=false" +
  "&isHighPriorityNetwork=false&isYearly=false&is2xProxies=false" +
  "&isCustomSubuserCount=false&isCustomOnDemandRefreshes=false";

export const HOME_PRODUCTS = {
  heading: "Our Proxy Solutions",
  body: "Webshare offers 3 different types of proxy products; Proxy Server (Datacenter), Static Residential (ISP), and Rotating Residential. Find what fits best for your needs.",
  cards: [
    {
      icon: `${ASSETS}/product-datacenter.svg`,
      title: "Proxy Servers",
      body: "400K+ Fast & Reliable IPs with 99.97% Uptime",
      price: "$2.99",
      period: "/month",
      accent: "purple" as const,
      cta: {
        label: "Start Free",
        href: `${REGISTER}?source=home_products_register`,
      } satisfies Cta,
    },
    {
      icon: `${ASSETS}/product-static-residential.svg`,
      title: "Static Residential",
      body: "Residential IPs from Comcast, AT&T & more with Up to Unlimited Bandwidth",
      price: "$6.00",
      period: "/month",
      accent: "accent" as const,
      cta: {
        label: "Buy Now",
        href:
          `${CHECKOUT}?source=home_products_buy&proxyType=%22shared%22` +
          "&proxySubtype=%22isp%22&showPlanDetails=true&proxyCount=20" +
          `&customProxyCount=100&${SHARED_PLAN}&proxyReplacements=10` +
          "&isCustomProxyReplacements=false&onDemandRefreshes=0&subuserCount=3" +
          "&customSubuserCount=3&bandwidth=250&siteChecks=%5B%5D" +
          "&customAutomaticRefreshFrequencyPeriod=%22h%22&countries=%7B%7D",
        newTab: true,
      } satisfies Cta,
    },
    {
      icon: `${ASSETS}/product-rotating-residential.svg`,
      title: "Rotating Residential",
      body: "Country, city, state, ZIP and ASN level targeting across 80 million+ real Residential IPs.",
      price: "$3.50",
      period: "/month",
      accent: "accent" as const,
      cta: {
        label: "Buy Now",
        href:
          `${CHECKOUT}?source=home_products_buy&proxyType=%22shared%22` +
          "&proxySubtype=%22residential%22&showPlanDetails=true" +
          `&proxyCount=30000000&customProxyCount=30000000&${SHARED_PLAN}` +
          "&proxyReplacements=0&isCustomProxyReplacements=false" +
          "&onDemandRefreshes=0&subuserCount=3&customSubuserCount=3" +
          "&bandwidth=1&siteChecks=%5B%5D" +
          "&customAutomaticRefreshFrequencyPeriod=%22h%22&countries=%7B%7D",
        newTab: true,
      } satisfies Cta,
    },
  ],
  banner: { title: "Looking for a custom solution?", label: "Contact Us" },
};

export const LOCATIONS = {
  heading: "Global Proxy Network",
  body: "80M+ IPs across 195 countries. Filter by country, state, city or ZIP code.",
  unit: "IPs",
};

export const RELIABLE = {
  chip: "Supported by Trusted Team in Silicon Valley",
  heading: "Proxies trusted at scale",
  body: "From your first free proxies to billions of requests, Webshare makes it simple to start and scale.",
  stats: [
    { value: "650B+", label: "Monthly proxy requests" },
    { value: "90K+", label: "Paying customers" },
    { value: "99.97%", label: "Network uptime" },
  ],
};

export const SUPPORT = {
  heading: "Best Customer Support",
  body: "Delivering Strategic Assistance for Optimal Performance",
  cards: [
    {
      image: `${ASSETS}/support-managers.avif`,
      /**
       * Webflow's resized copies stop short of the original on several of these
       * assets, `List-p-1600.avif` and `Thumbnail-p-2600.jpg` both answer 403:
       * so `srcsetWidth` is the first ladder rung ABOVE the last copy that
       * exists, not the original's own width. `srcsetFor` derives the rung list
       * from it, and under-declaring the original's width only ever makes a
       * browser pick a file at least as large as it asked for.
       */
      srcsetWidth: 1600,
      title: "Dedicated Account Managers",
      body: "Get tailored support with a dedicated account manager to help you succeed.",
    },
    {
      image: `${ASSETS}/support-experts.svg`,
      srcsetWidth: null,
      title: "24/7 Expert Support Whenever You Need It",
      body: "Our highly technical team is available 24/7 to support you",
    },
    {
      image: `${ASSETS}/support-help-center.svg`,
      srcsetWidth: null,
      title: "Webshare Help Center",
      body: "Your Guide to Navigating Webshare Features and Support",
    },
  ],
};

export const DEVELOPER = {
  heading: "Developer Friendly",
  body: "Seamless Integrations and Comprehensive API Docs for Effortless Implementation",
  /**
   * The only vendored asset with no `srcset`: its single Webflow rung is a
   * `.png` beside an `.avif` original, and `srcsetFor` builds the ladder from
   * the original's extension, so it cannot name that file.
   */
  apiCard: `${ASSETS}/api-card.avif`,
  apiCta: {
    label: "API",
    href: "https://apidocs.webshare.io/",
    newTab: true,
  } satisfies Cta,
  integrationsCta: {
    label: "Integrations",
    href: `${SITE}/integrations`,
    newTab: true,
  } satisfies Cta,
  /** Two tracks, the second running the other way. `wide` logos are 99×71. */
  marquees: [
    [
      { src: `${ASSETS}/integration-logo-4.svg` },
      { src: `${ASSETS}/integration-se.avif` },
      { src: `${ASSETS}/integration-logo-1.svg`, wide: true },
      { src: `${ASSETS}/integration-logo-2.svg` },
      { src: `${ASSETS}/integration-pupp.avif` },
      { src: `${ASSETS}/integration-logo-3.svg` },
    ],
    [
      { src: `${ASSETS}/integration-sm.avif` },
      { src: `${ASSETS}/integration-logo-11.svg` },
      { src: `${ASSETS}/integration-windows.avif` },
      { src: `${ASSETS}/integration-linux.avif` },
      { src: `${ASSETS}/integration-safari.avif` },
      { src: `${ASSETS}/integration-opera.svg` },
    ],
  ],
};

export const DASHBOARD = {
  heading: "Free Proxies and Custom Proxy List",
  body: "Start with 10 free proxies, tailor your plan to fit your usage, and track performance in real time with a powerful proxy dashboard.",
  /**
   * Each pane ships two crops: the wide one down to 768 and a portrait one
   * below it. Both are vendored; the swap is a media query, not a `<picture>`,
   * because that is what the published stylesheet does.
   */
  tabs: [
    {
      label: "Free Proxy",
      wide: `${ASSETS}/tab-free-proxy.avif`,
      narrow: `${ASSETS}/tab-free-proxy-mobile.webp`,
    },
    {
      label: "Browse Plans Page",
      wide: `${ASSETS}/tab-browse-plans.avif`,
      narrow: `${ASSETS}/tab-browse-plans-mobile.webp`,
    },
    {
      label: "Stats Page",
      wide: `${ASSETS}/tab-stats.avif`,
      narrow: `${ASSETS}/tab-stats-mobile.webp`,
    },
  ],
  /** See `SUPPORT.cards[0].srcsetWidth` for why these are not natural widths. */
  wideSrcsetWidth: 1600,
  narrowSrcsetWidth: 1080,
  cta: {
    heading: "Webshare offers permanent ",
    highlight: "free plan with 10 proxies.",
    label: "Get Webshare Free",
    href: `${REGISTER}?source=home_why_section_register`,
    newTab: true,
  },
};

const PREMIUM_PLAN =
  "isUnlimitedIpAuthorizations=false&isHighConcurrency=false" +
  "&isHighPriorityNetwork=false&proxyReplacements=10";
const PREMIUM_TAIL =
  "isCustomSubuserCount=false&siteChecks=%5B%5D&customSubuserCount=0";

export const PREMIUM = {
  heading: "Want Proxies That Are Yours Alone?",
  body: "Try Webshare private and dedicated proxies built for speed, consistency, and total control.",
  groups: [
    {
      icon: `${ASSETS}/premium-group-datacenter.svg`,
      title: "Proxy Servers",
      subtitle: "400K+ Fast & Reliable IPs with 99.97% Uptime",
      tone: "green" as const,
      cards: [
        {
          icon: `${ASSETS}/premium-private.svg`,
          title: "Private",
          subtitle: "Shared with 1-2 users",
          ips: "25 IPs",
          unitPrice: "$0.741",
          unit: "/IP",
          total: "$18.52",
          totalNote: "billed monthly",
          cta: {
            label: "Buy Now",
            href:
              `${CHECKOUT}?source=home_premium_products_buy` +
              `&proxyType=%22semidedicated%22&proxySubtype=%22premium%22` +
              `&${PREMIUM_PLAN}&countries=%7B%22ZZ%22:25%7D&${PREMIUM_TAIL}` +
              "&showPlanDetails=true&subuserCount=3&isCustomPlan=false" +
              "&isYearly=false&proxyCount=25&customProxyCount=50" +
              "&bandwidth=250&autoRefreshFrequency=0&onDemandRefreshes=0" +
              "&customPlanType=",
            newTab: true,
          } satisfies Cta,
        },
        {
          icon: `${ASSETS}/premium-dedicated.svg`,
          title: "Dedicated",
          subtitle: "Fully owned by you",
          ips: "20 IPs",
          unitPrice: "$1.33",
          unit: "/IP",
          total: "$26.60",
          totalNote: "billed monthly",
          cta: {
            label: "Buy Now",
            href:
              `${CHECKOUT}?source=home_premium_products_buy` +
              `&proxyType=%22dedicated%22&proxySubtype=%22premium%22` +
              `&${PREMIUM_PLAN}&countries=%7B%22ZZ%22:20%7D&${PREMIUM_TAIL}` +
              "&showPlanDetails=true&subuserCount=3&isCustomPlan=false" +
              "&isYearly=false&proxyCount=20&customProxyCount=75" +
              "&bandwidth=250&autoRefreshFrequency=0&onDemandRefreshes=0" +
              "&customPlanType=",
            newTab: true,
          } satisfies Cta,
        },
      ],
    },
    {
      icon: `${ASSETS}/premium-group-residential.svg`,
      title: "Static Residential",
      subtitle:
        "Residential IPs from Comcast, AT&T & more with Up to Unlimited Bandwidth",
      tone: "blue" as const,
      cards: [
        {
          icon: `${ASSETS}/premium-private.svg`,
          title: "Private",
          subtitle: "Shared with 1-2 users",
          ips: "25 IPs",
          unitPrice: "$0.926",
          unit: "/IP",
          total: "$23.16",
          totalNote: "billed monthly",
          /**
           * Reproduced verbatim, anomalies and all: this is the only premium
           * card whose source is `home_products_buy`, the only one without
           * `showPlanDetails=true`, and the only Buy Now on the page that does
           * not open a new tab. Attribution depends on the source key, so it is
           * not ours to tidy, it is raised with the page's owner instead.
           */
          cta: {
            label: "Buy Now",
            href:
              `${CHECKOUT}?source=home_products_buy` +
              `&proxyType=%22semidedicated%22&proxySubtype=%22isp%22` +
              `&${PREMIUM_PLAN}&countries=%7B%22ZZ%22:25%7D&${PREMIUM_TAIL}` +
              "&subuserCount=3&isCustomPlan=false&isYearly=false" +
              "&proxyCount=25&customProxyCount=50&bandwidth=250" +
              "&autoRefreshFrequency=0&onDemandRefreshes=0&customPlanType=",
          } satisfies Cta,
        },
        {
          icon: `${ASSETS}/premium-dedicated.svg`,
          title: "Dedicated",
          subtitle: "Fully owned by you",
          ips: "20 IPs",
          unitPrice: "$2.10",
          unit: "/IP",
          total: "$42.00",
          totalNote: "billed monthly",
          cta: {
            label: "Buy Now",
            href:
              `${CHECKOUT}?source=home_premium_products_buy` +
              `&proxyType=%22dedicated%22&proxySubtype=%22isp%22` +
              `&${PREMIUM_PLAN}&countries=%7B%22ZZ%22:20%7D&${PREMIUM_TAIL}` +
              "&showPlanDetails=true&subuserCount=3&isCustomPlan=false" +
              "&isYearly=false&proxyCount=20&customProxyCount=75" +
              "&bandwidth=250&autoRefreshFrequency=0&onDemandRefreshes=0" +
              "&customPlanType=",
            newTab: true,
          } satisfies Cta,
        },
      ],
    },
  ],
};

export const USE_CASES = {
  heading: "Built for Your Needs",
  panes: [
    {
      label: "Pricing Intelligence",
      body: "Webshare proxies will enable you to take your pricing intelligence to the next level. Implement price analysis, ecommerce scraping & take advantage of public product price data.",
      image: `${ASSETS}/usecase-pricing-intelligence.svg`,
      links: [
        {
          label: "Price Tracking & Monitoring",
          href: `${SITE}/use-cases/proxies-for-price-tracking-monitoring`,
        },
        { label: "Ecommerce", href: `${SITE}/use-cases/ecommerce-proxies` },
        {
          label: "Amazon proxies",
          href: `${SITE}/use-cases/amazon-proxy-server`,
        },
      ],
      cta: {
        label: "Learn More",
        href: `${SITE}/use-cases-categories/pricing-intelligence`,
        newTab: true,
      } satisfies Cta,
    },
    {
      label: "Anti-Fraud",
      body: "Webshare is a trusted Proxy provider among Ad Verification & Monitoring service providers. Our global proxy network will serve your technical needs.",
      image: `${ASSETS}/usecase-anti-fraud.svg`,
      links: [
        {
          label: "Ad Verification & Monitoring",
          href: `${SITE}/use-cases/proxies-for-ad-verification-monitoring`,
        },
        {
          label: "Cyber Security",
          href: `${SITE}/use-cases/proxies-for-cyber-security`,
        },
      ],
      cta: {
        label: "Learn More",
        href: `${SITE}/use-cases-categories/anti-fraud`,
        newTab: true,
      } satisfies Cta,
    },
    {
      label: "Social Media",
      body: "Proxies for social media account management, anonymous browsing and scraping are extremely popular among our users.",
      image: `${ASSETS}/usecase-social-media.svg`,
      links: [
        {
          label: "Social Media",
          href: `${SITE}/use-cases/social-media-proxies`,
        },
        { label: "Gaming", href: `${SITE}/use-cases/gaming-proxy-server` },
      ],
      cta: {
        label: "Learn More",
        href: `${SITE}/use-cases-categories/social-media`,
        newTab: true,
      } satisfies Cta,
    },
    {
      label: "Public Data",
      body: "Gather publicly available data for your business needs with the help of proxies. Conduct marketing, SEO or financial research without errors.",
      image: `${ASSETS}/usecase-public-data.svg`,
      links: [
        {
          label: "Web Scraping",
          href: `${SITE}/use-cases/web-scraping-data-extraction`,
        },
        { label: "SEO", href: `${SITE}/use-cases/seo-proxies` },
      ],
      cta: {
        label: "Learn More",
        href: `${SITE}/use-cases-categories/public-data`,
        newTab: true,
      } satisfies Cta,
    },
    {
      label: "Reputation Management",
      body: "Manage business or personal reputation at scale with assistance of proxies. Conduct continuous brand monitoring & market research.",
      image: `${ASSETS}/usecase-reputation-management.svg`,
      links: [
        {
          label: "Brand Monitoring",
          href: `${SITE}/use-cases/proxies-for-brand-monitoring`,
        },
        {
          label: "Market Research",
          href: `${SITE}/use-cases/proxies-for-market-research`,
        },
      ],
      cta: {
        label: "Learn More",
        href: `${SITE}/use-cases-categories/reputation-management`,
        newTab: true,
      } satisfies Cta,
    },
  ],
};

/**
 * The head, as `marketing/design/home-spec.md` recorded it.
 *
 * The canonical is the live root, not this path: while `/home` is a preview of
 * a page the published site still serves at `/`, claiming `/home` as canonical
 * would split the two. It flips with the route when this app owns the root.
 *
 * No trailing slash, because that is the site's own convention: every
 * server-rendered canonical on the published site is unslashed, the root
 * included, as are `/pricing` and `/blog`. The published root's DOM does carry
 * a second, slashed canonical, but it is injected client-side by CookieYes:
 * an anomaly on top of the served page rather than the page's own answer. Ours
 * emits exactly one, in the served form.
 */
export const HOME_HEAD = {
  title: "Webshare | Fast Proxies, Free to Start, Fair to Scale",
  description:
    "Rotating residential, static ISP, and datacenter proxies with a full API. 80M+ IPs, 195+ countries, 100+ Gbps backbone. Start with 10 free proxies, no credit card.",
  canonical: SITE,
  /**
   * The share card stays on the CMS host while the canonical does. It is the
   * one asset a scraper resolves rather than the browser, and it has to answer
   * from wherever the canonical points.
   */
  shareImage: "/marketing/webflow/67768f30fcfcfc72af150429_open-graph.webp",
};
